/**
 * Tile collision and continuous movement resolution.
 *
 * This module is shared verbatim between the server simulation and the client's
 * prediction. If the two ever disagree the player sees rubber banding, so nothing
 * here may depend on anything that differs between the two (no wall clock, no
 * random source, no floating point accumulation across frames).
 */

import { ENTITY_RADIUS, MAX_PLANE } from './constants';
import type { WorldPos } from './tiles';

export const CollisionFlag = {
  None: 0,
  /** Whole tile is impassable. */
  Blocked: 1 << 0,
  /**
   * A wall on one edge of a tile.
   *
   * North is negative z and south is positive z, the same as everywhere else,
   * so the north wall is the one on the low z side. The two used to be the
   * other way round, back when the camera rested facing the wrong way.
   */
  WallNorth: 1 << 1,
  WallEast: 1 << 2,
  WallSouth: 1 << 3,
  WallWest: 1 << 4,
  /** Blocks projectiles and line of sight but not movement, such as a low fence. */
  BlockProjectile: 1 << 5,
  /** Water, which nobody walks into. */
  Water: 1 << 6,
  /**
   * Boards laid over water: a dock, a pier, a bridge.
   *
   * The water is still there and still drawn, but there is something to stand
   * on, and that is the only way anybody crosses water on foot.
   */
  Decked: 1 << 7,
} as const;

export type CollisionFlagValue = (typeof CollisionFlag)[keyof typeof CollisionFlag];

/**
 * Whether a tile stops somebody, from its flags alone.
 *
 * Water stops them. An inch of water and a fathom of it look the same from
 * above and read the same to somebody trying to walk somewhere, so a shore
 * anybody can stand an inch into is a shore with no edge to it. The only way
 * across water on foot is something built over it, which is what the boards
 * say.
 *
 * Both the pathfinder and the continuous collision ask this, so what a path is
 * allowed to plan and what a body is allowed to occupy are the same rule.
 */
export function flagsBlock(flags: number): boolean {
  if ((flags & CollisionFlag.Blocked) !== 0) return true;
  return (flags & CollisionFlag.Water) !== 0 && (flags & CollisionFlag.Decked) === 0;
}

/** Thickness of a wall for collision purposes, in tiles. */
const WALL_THICKNESS = 0.12;

/** Separation kept between an entity and a surface, so contact never reads as overlap. */
const EPSILON = 1e-4;

interface Aabb {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/**
 * Collision flags for one region, one byte per tile per plane.
 *
 * Coordinates are world tiles throughout. The map knows where its region sits, so
 * the simulation never has to convert between world and region-local space, which
 * is exactly the kind of conversion that gets forgotten in one place out of five.
 */
export class CollisionMap {
  readonly width: number;
  readonly height: number;
  readonly originX: number;
  readonly originZ: number;
  private readonly planes: Uint8Array[];

  /*
   * Tiles that are different for whoever is looking, by where they are.
   *
   * Nothing for the world itself, which is the usual case and costs nothing to
   * ask about. A view made for one person carries only what differs: a world
   * where one gate stands open for one player is a world with one tile in it
   * that is not the same for everybody and three million that are.
   */
  private own: ReadonlyMap<number, number> | null = null;

  constructor(width: number, height: number, originX = 0, originZ = 0) {
    this.width = width;
    this.height = height;
    this.originX = originX;
    this.originZ = originZ;
    this.planes = [];
    for (let plane = 0; plane <= MAX_PLANE; plane++) {
      this.planes.push(new Uint8Array(width * height));
    }
  }

  inBounds(tx: number, tz: number): boolean {
    const lx = tx - this.originX;
    const lz = tz - this.originZ;
    return lx >= 0 && lz >= 0 && lx < this.width && lz < this.height;
  }

  /**
   * Where a tile sits in the one numbering a view's own tiles are kept by.
   *
   * Public because the thing that builds a view has to speak the same language
   * as the thing that reads it, and two ways of saying where a tile is would be
   * two ways to get it wrong.
   */
  keyOf(tx: number, tz: number, plane: number): number {
    const lx = tx - this.originX;
    const lz = tz - this.originZ;
    return (plane * this.height + lz) * this.width + lx;
  }

  /**
   * The same ground, as one person sees it.
   *
   * Shares every array with the map it came from and carries only what is
   * different, so making one costs a map lookup rather than a copy of the
   * continent. Everything that asks about the ground asks through flagsAt, so
   * walking, pathfinding, line of sight and what is sent to the client all come
   * out of this without any of them knowing it exists.
   */
  seenBy(own: ReadonlyMap<number, number>): CollisionMap {
    if (own.size === 0) return this;

    // Sharing the prototype chain rather than copying: the view is the map
    // with one field of its own, and the field is private to this class, which
    // is why it is written from inside it.
    const view: CollisionMap = Object.create(this) as CollisionMap;
    view.own = own;
    return view;
  }

  flagsAt(tx: number, tz: number, plane: number): number {
    if (!this.inBounds(tx, tz)) return CollisionFlag.Blocked;

    if (this.own) {
      const held = this.own.get(this.keyOf(tx, tz, plane));
      if (held !== undefined) return held;
    }

    const data = this.planes[plane];
    if (!data) return CollisionFlag.Blocked;
    return data[(tz - this.originZ) * this.width + (tx - this.originX)] ?? CollisionFlag.Blocked;
  }

  setFlags(tx: number, tz: number, plane: number, flags: number): void {
    if (!this.inBounds(tx, tz)) return;
    const data = this.planes[plane];
    if (!data) return;
    data[(tz - this.originZ) * this.width + (tx - this.originX)] = flags;
  }

  addFlags(tx: number, tz: number, plane: number, flags: number): void {
    this.setFlags(tx, tz, plane, this.flagsAt(tx, tz, plane) | flags);
  }

  clearFlags(tx: number, tz: number, plane: number, flags: number): void {
    this.setFlags(tx, tz, plane, this.flagsAt(tx, tz, plane) & ~flags);
  }

  /**
   * A copy of one plane's flags, row major from the region origin.
   *
   * This is what goes to clients, rather than the authored region data, because it
   * already has object footprints stamped into it. Sending the authored flags would
   * mean the client had to redo that derivation and agree with the server exactly,
   * and any disagreement shows up as a player walking into a tree on their screen
   * and being pushed back out.
   */
  exportPlane(plane: number): number[] {
    const data = this.planes[plane];
    if (!data) return new Array<number>(this.width * this.height).fill(CollisionFlag.Blocked);
    return Array.from<number>(data);
  }

  /**
   * One square of a plane, in the square's own order.
   *
   * The whole plane of a continent is millions of bytes and nobody near the
   * middle of it needs the edges. A square with nothing above it comes back as
   * solid, which is what a floor that was never built is.
   */
  exportSquare(plane: number, tx: number, tz: number, width: number, height: number): Uint8Array {
    const held = new Uint8Array(width * height);
    const data = this.planes[plane];

    if (!data) return held.fill(CollisionFlag.Blocked);

    const fromX = tx - this.originX;
    const fromZ = tz - this.originZ;

    for (let z = 0; z < height; z++) {
      const row = (fromZ + z) * this.width + fromX;
      for (let x = 0; x < width; x++) held[z * width + x] = data[row + x] ?? CollisionFlag.Blocked;
    }

    // And whatever is different for whoever this is for, laid over the top. The
    // client predicts its own movement from exactly these flags, so a view that
    // walked differently from what it was sent would be a player stopped by a
    // gate they can see standing open.
    if (this.own) {
      for (const [at, flags] of this.own) {
        if (Math.floor(at / (this.width * this.height)) !== plane) continue;

        const inPlane = at % (this.width * this.height);
        const z = Math.floor(inPlane / this.width) - fromZ;
        const x = (inPlane % this.width) - fromX;
        if (x < 0 || z < 0 || x >= width || z >= height) continue;

        held[z * width + x] = flags;
      }
    }

    return held;
  }

  /** Whether a tile can be occupied at all. Used by pathfinding. */
  isBlocked(tx: number, tz: number, plane: number): boolean {
    return flagsBlock(this.flagsAt(tx, tz, plane));
  }

  /** Whether an entity can step between two adjacent tiles, respecting walls. */
  canStep(fromX: number, fromZ: number, toX: number, toZ: number, plane: number): boolean {
    if (this.isBlocked(toX, toZ, plane)) return false;

    const dx = toX - fromX;
    const dz = toZ - fromZ;
    if (dx === 0 && dz === 0) return true;

    if (dx !== 0 && dz !== 0) {
      // A diagonal step is only legal if both cardinal components are legal, which
      // is what stops entities slipping through the corner where two walls meet.
      return (
        this.canStep(fromX, fromZ, fromX + dx, fromZ, plane) &&
        this.canStep(fromX, fromZ, fromX, fromZ + dz, plane) &&
        this.canStep(fromX + dx, fromZ, toX, toZ, plane) &&
        this.canStep(fromX, fromZ + dz, toX, toZ, plane)
      );
    }

    const from = this.flagsAt(fromX, fromZ, plane);
    const to = this.flagsAt(toX, toZ, plane);

    if (dz > 0)
      return (from & CollisionFlag.WallSouth) === 0 && (to & CollisionFlag.WallNorth) === 0;
    if (dz < 0)
      return (from & CollisionFlag.WallNorth) === 0 && (to & CollisionFlag.WallSouth) === 0;
    if (dx > 0) return (from & CollisionFlag.WallEast) === 0 && (to & CollisionFlag.WallWest) === 0;
    return (from & CollisionFlag.WallWest) === 0 && (to & CollisionFlag.WallEast) === 0;
  }

  /** Solid boxes overlapping the given tile range, for continuous collision. */
  private collectObstacles(
    minTx: number,
    maxTx: number,
    minTz: number,
    maxTz: number,
    plane: number,
    out: Aabb[],
  ): void {
    const half = WALL_THICKNESS / 2;
    for (let tz = minTz; tz <= maxTz; tz++) {
      for (let tx = minTx; tx <= maxTx; tx++) {
        const flags = this.flagsAt(tx, tz, plane);
        if (flags === CollisionFlag.None) continue;

        if (flagsBlock(flags)) {
          out.push({ minX: tx, maxX: tx + 1, minZ: tz, maxZ: tz + 1 });
          continue;
        }
        if ((flags & CollisionFlag.WallSouth) !== 0) {
          out.push({ minX: tx, maxX: tx + 1, minZ: tz + 1 - half, maxZ: tz + 1 + half });
        }
        if ((flags & CollisionFlag.WallNorth) !== 0) {
          out.push({ minX: tx, maxX: tx + 1, minZ: tz - half, maxZ: tz + half });
        }
        if ((flags & CollisionFlag.WallEast) !== 0) {
          out.push({ minX: tx + 1 - half, maxX: tx + 1 + half, minZ: tz, maxZ: tz + 1 });
        }
        if ((flags & CollisionFlag.WallWest) !== 0) {
          out.push({ minX: tx - half, maxX: tx + half, minZ: tz, maxZ: tz + 1 });
        }
      }
    }
  }

  /** Whether a circle at this position intersects anything solid. */
  overlaps(x: number, z: number, plane: number, radius = ENTITY_RADIUS): boolean {
    const obstacles: Aabb[] = [];
    this.collectObstacles(
      Math.floor(x - radius - 1),
      Math.floor(x + radius + 1),
      Math.floor(z - radius - 1),
      Math.floor(z + radius + 1),
      plane,
      obstacles,
    );
    for (const box of obstacles) {
      if (x + radius <= box.minX || x - radius >= box.maxX) continue;
      if (z + radius <= box.minZ || z - radius >= box.maxZ) continue;
      return true;
    }
    return false;
  }

  /** Whether an entity of the given radius can travel a straight line between two points. */
  canTraverse(
    fromX: number,
    fromZ: number,
    toX: number,
    toZ: number,
    plane: number,
    radius = ENTITY_RADIUS,
  ): boolean {
    const dx = toX - fromX;
    const dz = toZ - fromZ;
    const distance = Math.sqrt(dx * dx + dz * dz);
    const steps = Math.max(1, Math.ceil(distance / 0.15));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      if (this.overlaps(fromX + dx * t, fromZ + dz * t, plane, radius)) return false;
    }
    return true;
  }

  /**
   * Advances a position by a delta, resolving collision one axis at a time so that
   * running into a wall at an angle slides along it rather than stopping dead.
   *
   * Axis separation is only correct while the entity never overlaps an obstacle to
   * begin with, which is why every push-out leaves an epsilon of clearance.
   */
  resolveMovement(from: WorldPos, dx: number, dz: number, radius = ENTITY_RADIUS): WorldPos {
    let x = from.x;
    let z = from.z;
    const plane = from.plane;

    const startTx = Math.floor(x);
    const startTz = Math.floor(z);

    const obstacles: Aabb[] = [];
    const reach = radius + Math.max(Math.abs(dx), Math.abs(dz)) + 1;
    this.collectObstacles(
      Math.floor(x - reach),
      Math.floor(x + reach),
      Math.floor(z - reach),
      Math.floor(z + reach),
      plane,
      obstacles,
    );

    if (dx !== 0) {
      const wasX = x;
      x += dx;
      for (const box of obstacles) {
        if (z + radius <= box.minZ || z - radius >= box.maxZ) continue;
        if (x + radius <= box.minX || x - radius >= box.maxX) continue;
        x = dx > 0 ? box.minX - radius - EPSILON : box.maxX + radius + EPSILON;
      }

      // The tile a player stands on is their position as far as every rule measured
      // in ticks is concerned, so it may never be a blocked one. Sliding along a
      // surface can otherwise carry the centre across a corner into a tile the
      // pathfinder would refuse, and once inside, every following step resolves from
      // a position that overlaps something, which is where the sliding comes from.
      if (this.enteredBlockedTile(startTx, startTz, x, z, plane)) x = wasX;
    }

    if (dz !== 0) {
      const wasZ = z;
      z += dz;
      for (const box of obstacles) {
        if (x + radius <= box.minX || x - radius >= box.maxX) continue;
        if (z + radius <= box.minZ || z - radius >= box.maxZ) continue;
        z = dz > 0 ? box.minZ - radius - EPSILON : box.maxZ + radius + EPSILON;
      }

      if (this.enteredBlockedTile(startTx, startTz, x, z, plane)) z = wasZ;
    }

    // Never leave the region, whatever the collision data says.
    x = Math.min(
      Math.max(x, this.originX + radius + EPSILON),
      this.originX + this.width - radius - EPSILON,
    );
    z = Math.min(
      Math.max(z, this.originZ + radius + EPSILON),
      this.originZ + this.height - radius - EPSILON,
    );

    return { x, z, plane };
  }

  /**
   * Whether a position has moved onto a blocked tile it was not already on.
   *
   * Already being on one is not treated as entering it: an entity that somehow ends
   * up inside geometry has to be able to walk back out, and refusing every move
   * would wedge it there permanently.
   */
  private enteredBlockedTile(
    fromTx: number,
    fromTz: number,
    x: number,
    z: number,
    plane: number,
  ): boolean {
    const tx = Math.floor(x);
    const tz = Math.floor(z);
    if (tx === fromTx && tz === fromTz) return false;
    return this.isBlocked(tx, tz, plane);
  }
}
